import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import config from "@/config";
import useCache from "@/hooks/useCache";
import useProtectActionUnregistered from '@/hooks/useProtectActionUnregistered';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import NavbarHomeNavigation from "@/layouts/navigation/components/NavbarHomeNavigation";
import fetchWrapper from "@/services/fetchWrapper.service";
import HomeCarouselAnalyticsService from "@/services/homeCarouselAnalytics.service";
import HomeNavigation from "@/shared-components/home/components/HomeNavigation";
import Loader from "@/shared-components/loader/Loader";
import UnregisteredPopup from "@/shared-components/popups/unregistered-user-popup";
import StaticSeoHead from "@/shared-components/seo/StaticSeoHead";
import AppNavigationPage from "@/shared-components/wrappers/components/AppNavigationPage";
import { getSsrAuthBootstrap } from "@/utils/ssrAuthBootstrap";

import ArtistSlider from "./components/HomeArtists";
import HomeArtlists from "./components/HomeArtlists";
import HomeAuctionBanner from "./components/HomeAuctionBanner";
import HomeBanner from "./components/HomeBanner";
import HomeCategories from "./components/HomeCategories";
import HomeChannels from "./components/HomeChannels";
import GallerySlider from "./components/HomeGalleries";
import HomeLimitedEditions from "./components/HomeLimitedEditions";
import HomeLinks from "./components/HomeLinks";
import HomeSlider from "./components/HomeSlider";
import HomeSliderSmall from "./components/HomeSliderSmall";
import UserSlider from "./components/HomeUsers";

import "./index.css";

function readInitialAuthSeoPayload(routeKind) {
  if (!routeKind) {
    return null;
  }

  const payload = getSsrAuthBootstrap(routeKind);
  return payload?.seoData && typeof payload.seoData === 'object' ? payload.seoData : null;
}

function HomeContent({ fetchData, isMobileViewport, onRetry }) {
  const tHome = useTranslations('common.shared.home');
  const navigate = useNavigate();
  const { privateActionHandlerUnregistered, showPopup } = useProtectActionUnregistered({ typeOfProtection: "USER_LIMIT_VIEW" });
  const collections = Array.isArray(fetchData?.collections) ? fetchData.collections : null;

  if (!collections) {
    return (
      <section className="home-discover__load-error" role="alert">
        <p>We couldn’t load the homepage. Please try again.</p>
        <button type="button" onClick={() => void onRetry?.().catch(() => undefined)}>
          Try again
        </button>
      </section>
    );
  }

  function handleClick(event) {
    privateActionHandlerUnregistered(event);
    if (event.defaultPrevented) {
      return;
    }
  }

  // Share the same click logic for classic and auction banners
  const buildBannerOnClick = (slug) => (event) => {
    handleClick(event);
    if (!event.defaultPrevented && slug) {
      navigate(`/${slug}`);
    }
  };

  return (
    <>
      {showPopup && <UnregisteredPopup showPopUp={showPopup} />}
      {collections.map((slider) => {
        const trackingContext = {
          collectionId: slider.collection_id,
          collectionName: slider.collection_name,
          carouselType: slider.type,
          contentMode: slider.content_mode,
          position: isMobileViewport ? slider.order_mobile : slider.order,
          isMobile: isMobileViewport,
        };

        switch (slider.type) {
        case "banner": {
          const banner = Array.isArray(slider.collection_banner)
            ? slider.collection_banner[0] || {}
            : {};
          const {
            image_mobile,
            image_desktop,
            slug,
            description,
            cta,
          } = banner;
          const title = slider.collection_name;

          return (
            <div className="full-bleed" key={slider.collection_id}>
              <HomeBanner
                mobileImage={image_mobile}
                desktopImage={image_desktop}
                alt={description}
                title={title}
                description={description}
                buttonText={cta || tHome('discover')}
                onClick={buildBannerOnClick(slug)}
              />
            </div>
          );
        }

        case "banner-event": {
          const banner = Array.isArray(slider.collection_banner_events)
            ? slider.collection_banner_events[0] || {}
            : {};
          const { image_mobile, image_desktop, slug } = banner;

          return (
            <div className="full-bleed" key={slider.collection_id}>
              <HomeAuctionBanner
                mobileImage={image_mobile}
                desktopImage={image_desktop}
                onClick={buildBannerOnClick(slug)}
              />
            </div>
          );
        }

        case "artworks-small":
          return (
            <HomeSliderSmall
              key={`${slider.collection_id}-small`}
              artworks={slider.collection_artworks}
              category={slider.collection_name}
              endpoint={`/category/${slider.collection_id}?`}
              total={slider.total}
              link={slider.collection_url}
              contentMode={slider.content_mode}
              trackingContext={trackingContext}
            />
          );

        case "artlists":
          return (
            <HomeArtlists
              key={slider.collection_id}
              artlists={slider.collection_artlists}
              title={slider.collection_name}
              moreLink={slider.collection_url}
              trackingContext={trackingContext}
            />
          );

        case "users":
          return (
            <UserSlider
              key={slider.collection_id}
              users={slider.collection_users}
              title={slider.collection_name}
              moreLink={slider.collection_url}
              trackingContext={trackingContext}
            />
          );

        case "galleries":
          return (
            <GallerySlider
              key={slider.collection_id}
              users={slider.collection_users}
              title={slider.collection_name}
              moreLink={slider.collection_url}
              trackingContext={trackingContext}
            />
          );

        case "artists":
          return (
            <ArtistSlider
              key={slider.collection_id}
              galleryartists={slider.collection_artists}
              title={slider.collection_name}
              moreLink={slider.collection_url}
              trackingContext={trackingContext}
            />
          );

        case "channels":
          return (
            <HomeChannels
              key={slider.collection_id}
              channels={slider.collection_channels}
              title={slider.collection_name}
              trackingContext={trackingContext}
            />
          );

        case "category": {
          const cats =
              slider.collection_category || slider.collection_categories || [];
          return (
            <HomeCategories
              key={slider.collection_id}
              categories={cats}
              collection_type={slider.collection_type}
              title={slider.collection_name}
              moreLink={slider.collection_url}
              trackingContext={trackingContext}
            />
          );
        }

        case "artworks":
          if (String(slider.collection_id || "").toLowerCase() === "limited_editions") {
            return (
              <HomeLimitedEditions
                key={slider.collection_id}
                artworks={slider.collection_artworks}
                title={slider.collection_name}
                contentMode={slider.content_mode}
                trackingContext={trackingContext}
              />
            );
          }
          return (
            <HomeSlider
              key={slider.collection_id}
              artworks={slider.collection_artworks}
              category={slider.collection_name}
              endpoint={`/category/${slider.collection_id}?`}
              total={slider.total}
              link={slider.collection_url}
              contentMode={slider.content_mode}
              trackingContext={trackingContext}
            />
          );
        default:
          return (
            <HomeSlider
              key={slider.collection_id}
              artworks={slider.collection_artworks}
              category={slider.collection_name}
              endpoint={`/category/${slider.collection_id}?`}
              total={slider.total}
              link={slider.collection_url}
              contentMode={slider.content_mode}
              trackingContext={trackingContext}
            />
          );
        }
      })}
    </>
  );
}

export default function HomeDiscover({
  showAuthPopup = false,
  authPopUpPage = "",
}) {
  const { state, stateHandler } = useStateHandler();
  const navigate = useNavigate();
  const isMobileViewport = window.innerWidth <= 550;
  const hasTrackedHomeViewRef = useRef(false);
  const authRouteKind = useMemo(
    () => (showAuthPopup && (authPopUpPage === "login" || authPopUpPage === "sign-up") ? authPopUpPage : ""),
    [authPopUpPage, showAuthPopup]
  );
  const [authSeo, setAuthSeo] = useState(() => readInitialAuthSeoPayload(authRouteKind));

  const { loading, fetchData, refetch } = useCache(
    `home_discover_${isMobileViewport ? "mobile" : "desktop"}`,
    `${config.apis.api.url}/home/${isMobileViewport}`,
    {
      expiresIn: ["10", "minutes"],
    }
  );

  useEffect(() => {
    if (showAuthPopup && authPopUpPage) {
      if (!state.user_session) {
        stateHandler.set("showAuthPopup", authPopUpPage);
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [showAuthPopup, authPopUpPage, state.user_session]);

  useEffect(() => {
    if (loading || hasTrackedHomeViewRef.current) {
      return;
    }

    const collections = Array.isArray(fetchData?.collections)
      ? fetchData.collections
      : [];

    if (collections.length === 0) {
      return;
    }

    hasTrackedHomeViewRef.current = true;

    HomeCarouselAnalyticsService.trackHomeDiscoverView(
      { isMobile: isMobileViewport },
      { userSession: state.user_session }
    );
  }, [loading, fetchData, isMobileViewport, state.user_session]);

  useEffect(() => {
    if (!authRouteKind) {
      setAuthSeo(null);
      return;
    }

    let ignore = false;

    async function loadAuthSeo() {
      try {
        const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/seo/auth/${encodeURIComponent(authRouteKind)}`);
        if (ignore || !response?.ok || !data || typeof data !== 'object') {
          return;
        }

        setAuthSeo(data);
      } catch (error) {
        if (!ignore) {
          console.error('Failed to load auth SEO payload', error);
        }
      }
    }

    loadAuthSeo();

    return () => {
      ignore = true;
    };
  }, [authRouteKind]);

  return (
    <>
      <StaticSeoHead
        title={authSeo?.title || "Suarte | Buy art from galleries worldwide"}
        description={authSeo?.metaDescription || "The social art sales platform. Buy original paintings and wall sculptures online from art galleries worldwide. Register today and experience contemporary and modern fine art in the most transparent marketplace, connecting with a community of art lovers. Discover your gateway to the art world and start or keep growing your collection with ease."}
        canonicalUrl={authSeo?.canonicalUrl}
        canonicalPath={authSeo?.canonicalPath || "/"}
        robots={authSeo?.robots}
        ogType={authSeo?.ogType}
        image={authSeo?.ogImage}
        pageType={authSeo ? "WebPage" : "CollectionPage"}
        structuredData={authSeo?.structuredData}
        breadcrumbItems={authSeo ? undefined : [
          { name: 'Home', url: 'https://suarte.art/' },
        ]}
      />
      <AppNavigationPage>
        <div className="home-discover__page">
          <HomeNavigation active="home" />
          <div className="home-discover__navbar">
            <NavbarHomeNavigation active="home" />
          </div>
          <HomeLinks />
          {loading ? <Loader /> : <HomeContent fetchData={fetchData} isMobileViewport={isMobileViewport} onRetry={refetch} />}
        </div>
      </AppNavigationPage>
      
    </>
  );
}
